import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Pwa } from '@freelancer/pwa';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { ModalService } from '../modal';
import { UI_CONFIG } from '../ui.config';
import { UiConfig } from '../ui.interface';

@UntilDestroy({ className: 'UpdateNativeAlertComponent' })
@Component({
  standalone: false,
  selector: 'fl-update-native-alert',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UpdateNativeAlertComponent implements OnInit {
  private pwa = inject(Pwa);
  private modalService = inject(ModalService);
  uiConfig = inject<UiConfig>(UI_CONFIG);

  async ngOnInit(): Promise<void> {
    if (await this.needsToUpdate()) {
      this.modalService.open('UpdateNativeModal', { closeable: false });
    }
  }

  /**
   * Whether to force the user to update their native app.
   *
   * Note: there are UI tests for this for both apps (native-update.spec)
   * you update any of this logic, update the tests
   */
  async needsToUpdate(): Promise<boolean> {
    if (this.pwa.isNative()) {
      const appVersion = await firstValueFrom(
        this.pwa.appVersion().pipe(untilDestroyed(this)),
      ).catch(() => undefined);

      // If Capacitor plugins didn't load properly, app version may be undefined.
      // In that case, don't show the update modal because they could be on the latest version.
      if (!appVersion || !appVersion.nativeVersion) {
        return false;
      }

      // Freelancer and Loadshift apps have different version numbers.
      switch (this.uiConfig.theme) {
        case undefined:
          return !(await this.pwa.hasMinimumNativeAppVersion({
            major: 6,
            minor: 4,
          }));
        case 'freightlancer':
          return !(await this.pwa.hasMinimumNativeAppVersion({
            major: 22,
            minor: 4,
          }));
          return false;
        default:
          return false;
      }
    }

    return false;
  }
}
